import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

/**
 * Markdown/Text Editor Component
 * Provides editing capabilities for .md and .txt files
 * Saves in-place to original file using File System Access API
 */

const MarkdownEditor = ({ file, onSave, onClose }) => {
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (file && file.content) {
      const fileContent = typeof file.content === 'string' ? file.content : '';
      setContent(fileContent);
      setOriginalContent(fileContent);
      setHasChanges(false);
    }
  }, [file]);

  useEffect(() => {
    // Check for changes
    setHasChanges(content !== originalContent);
  }, [content, originalContent]);

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    
    try {
      await onSave(file, content);
      setOriginalContent(content);
      setHasChanges(false);
      toast.success(`Saved ${file.name}`);
    } catch (error) {
      toast.error(`Failed to save ${file.name}: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAs = async () => {
    try {
      // Use File System Access API to save as new file
      const newFileName = prompt('Enter new file name:', file.name.replace(/\.[^/.]+$/, '') + '_copy' + file.extension);
      if (!newFileName) return;

      const fileHandle = await window.showSaveFilePicker({
        suggestedName: newFileName,
        types: file.extension === '.md' ? [
          {
            description: 'Markdown files',
            accept: { 'text/markdown': ['.md'] }
          }
        ] : [
          {
            description: 'Text files',
            accept: { 'text/plain': ['.txt'] }
          }
        ]
      });

      const writable = await fileHandle.createWritable();
      await writable.write(content);
      await writable.close();

      toast.success(`Saved as ${newFileName}`);
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error(`Failed to save as: ${error.message}`);
      }
    }
  };

  const handleKeyDown = (e) => {
    // Save shortcut: Ctrl+S or Cmd+S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
    
    // Escape to close
    if (e.key === 'Escape') {
      if (hasChanges) {
        if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
          onClose();
        }
      } else {
        onClose();
      }
    }
  };

  const insertMarkdown = (markdown) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText = '';
    let cursorOffset = 0;

    switch (markdown) {
      case 'bold':
        newText = `**${selectedText || 'bold text'}**`;
        cursorOffset = selectedText ? 0 : -5;
        break;
      case 'italic':
        newText = `*${selectedText || 'italic text'}*`;
        cursorOffset = selectedText ? 0 : -6;
        break;
      case 'code':
        newText = `\`${selectedText || 'code'}\``;
        cursorOffset = selectedText ? 0 : -2;
        break;
      case 'link':
        newText = `[${selectedText || 'link text'}](url)`;
        cursorOffset = selectedText ? -5 : -9;
        break;
      case 'heading':
        newText = `# ${selectedText || 'Heading'}`;
        cursorOffset = selectedText ? 0 : -4;
        break;
      case 'list':
        newText = `- ${selectedText || 'List item'}`;
        cursorOffset = selectedText ? 0 : -6;
        break;
      case 'quote':
        newText = `> ${selectedText || 'Quote'}`;
        cursorOffset = selectedText ? 0 : -3;
        break;
      default:
        return;
    }

    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + newText.length + cursorOffset;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const renderPreview = () => {
    const escapeHtml = (text = '') =>
      text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const renderInlineMarkdown = (text = '') =>
      escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(
          /\[([^\]]+)\]\(([^)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
        );

    const parseTableRow = (row = '') =>
      row
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

    const tableSeparatorRegex = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/;

    const lines = content.split('\n');
    const blocks = [];

    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        i += 1;
        continue;
      }

      // Markdown table (GFM-style)
      const nextLine = lines[i + 1]?.trim() || '';
      if (line.includes('|') && tableSeparatorRegex.test(nextLine)) {
        const headers = parseTableRow(line);
        const alignmentCells = parseTableRow(nextLine);
        const alignments = headers.map((_, index) => {
          const token = (alignmentCells[index] || '').trim();
          if (token.startsWith(':') && token.endsWith(':')) return 'center';
          if (token.endsWith(':')) return 'right';
          return 'left';
        });

        i += 2;
        const rows = [];
        while (i < lines.length) {
          const rowLine = lines[i];
          if (!rowLine.trim() || !rowLine.includes('|')) break;
          rows.push(parseTableRow(rowLine));
          i += 1;
        }

        const headerHtml = headers
          .map(
            (header, index) =>
              `<th style="text-align:${alignments[index]};">${renderInlineMarkdown(header)}</th>`
          )
          .join('');

        const bodyHtml = rows
          .map((row) => {
            const cells = headers
              .map(
                (_, index) =>
                  `<td style="text-align:${alignments[index]};">${renderInlineMarkdown(row[index] || '')}</td>`
              )
              .join('');
            return `<tr>${cells}</tr>`;
          })
          .join('');

        blocks.push(
          `<div class="md-table-wrapper"><table class="md-table"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`
        );
        continue;
      }

      if (/^###\s+/.test(trimmed)) {
        blocks.push(`<h3>${renderInlineMarkdown(trimmed.replace(/^###\s+/, ''))}</h3>`);
        i += 1;
        continue;
      }

      if (/^##\s+/.test(trimmed)) {
        blocks.push(`<h2>${renderInlineMarkdown(trimmed.replace(/^##\s+/, ''))}</h2>`);
        i += 1;
        continue;
      }

      if (/^#\s+/.test(trimmed)) {
        blocks.push(`<h1>${renderInlineMarkdown(trimmed.replace(/^#\s+/, ''))}</h1>`);
        i += 1;
        continue;
      }

      if (/^-\s+/.test(trimmed)) {
        const items = [];
        while (i < lines.length && /^-\s+/.test(lines[i].trim())) {
          items.push(lines[i].trim().replace(/^-\s+/, ''));
          i += 1;
        }

        blocks.push(`<ul>${items.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join('')}</ul>`);
        continue;
      }

      if (/^>\s+/.test(trimmed)) {
        const quoteLines = [];
        while (i < lines.length && /^>\s+/.test(lines[i].trim())) {
          quoteLines.push(lines[i].trim().replace(/^>\s+/, ''));
          i += 1;
        }

        blocks.push(`<blockquote>${quoteLines.map((q) => renderInlineMarkdown(q)).join('<br>')}</blockquote>`);
        continue;
      }

      const paragraphLines = [trimmed];
      i += 1;

      while (i < lines.length) {
        const current = lines[i];
        const currentTrimmed = current.trim();
        const following = lines[i + 1]?.trim() || '';

        const startsNewBlock =
          !currentTrimmed ||
          /^#{1,3}\s+/.test(currentTrimmed) ||
          /^-\s+/.test(currentTrimmed) ||
          /^>\s+/.test(currentTrimmed) ||
          (current.includes('|') && tableSeparatorRegex.test(following));

        if (startsNewBlock) break;

        paragraphLines.push(currentTrimmed);
        i += 1;
      }

      blocks.push(`<p>${renderInlineMarkdown(paragraphLines.join(' '))}</p>`);
    }

    return { __html: blocks.join('') || '<p></p>' };
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`theme-modal-backdrop fixed inset-0 flex items-center justify-center z-50 p-4 ${
        isMaximized ? 'p-0' : ''
      }`}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className={`theme-modal rounded-lg shadow-2xl flex flex-col ${
          isMaximized
            ? 'w-full h-full rounded-none'
            : 'w-full max-w-7xl max-h-[95vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{file.name}</h2>
              <p className="text-sm text-gray-500">
                {file.extension === '.md' ? 'Markdown' : 'Text'} Editor
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {hasChanges && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                Unsaved changes
              </span>
            )}
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">
            {file.extension === '.md' && (
              <>
                <button
                  onClick={() => insertMarkdown('bold')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  title="Bold (Ctrl+B)"
                >
                  <strong>B</strong>
                </button>
                <button
                  onClick={() => insertMarkdown('italic')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  title="Italic (Ctrl+I)"
                >
                  <em>I</em>
                </button>
                <button
                  onClick={() => insertMarkdown('code')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors font-mono text-sm"
                  title="Code"
                >
                  {'</>'}
                </button>
                <div className="w-px h-6 bg-gray-300" />
                <button
                  onClick={() => insertMarkdown('heading')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  title="Heading"
                >
                  H1
                </button>
                <button
                  onClick={() => insertMarkdown('list')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  title="List"
                >
                  •
                </button>
                <button
                  onClick={() => insertMarkdown('quote')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  title="Quote"
                >
                  "
                </button>
                <button
                  onClick={() => insertMarkdown('link')}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  title="Link"
                >
                  🔗
                </button>
                <div className="w-px h-6 bg-gray-300" />
              </>
            )}
            
            {file.extension === '.md' && (
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className={`p-2 rounded transition-colors ${
                  isPreviewMode ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-200'
                }`}
                title="Toggle Preview"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSaveAs}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Save As
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={`px-4 py-1.5 text-sm rounded transition-colors ${
                hasChanges && !isSaving
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Editor/Preview Area */}
        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            {!isPreviewMode ? (
              <motion.div
                key="editor"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-full"
              >
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={handleContentChange}
                  onKeyDown={handleKeyDown}
                  className="w-full h-full p-4 font-mono text-sm resize-none focus:outline-none"
                  placeholder={file.extension === '.md' ? 'Enter markdown content...' : 'Enter text content...'}
                  spellCheck={file.extension !== '.md'}
                />
              </motion.div>
            ) : (
              <motion.div
                key="preview"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="markdown-preview h-full overflow-auto p-6 text-sm text-gray-800"
                dangerouslySetInnerHTML={renderPreview()}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between p-2 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
          <div>
            Lines: {content.split('\n').length} | Characters: {content.length}
          </div>
          <div>
            {hasChanges ? 'Unsaved' : 'Saved'} | Ctrl+S to save | Esc to close
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MarkdownEditor;
